/**
 * Pieces the league screens share (docs/game-design.md §7, designs/Caged League, Leagues, Tiers):
 * division titles, the fight-time chip, results, flags.
 */
import { fightsOn, type DayInfo, type FixtureView, type SeriesInfo, type SeriesView } from '@caged/shared';
import { useTranslation } from 'react-i18next';
import { countryName, flagEmoji } from '../hud/format';
import { useDuration } from './components/ProgressWidgets';
import { formatNumber } from './format';
import s from './screens/League.module.css';

/** "Contender IV.12"; "Legend" for Division I. */
export function useSeriesTitle() {
  const { t } = useTranslation();
  return (series: Pick<SeriesInfo, 'name' | 'label' | 'division'>) => {
    const name = t(`screens.league.divisions.${series.name}`);
    return series.division === 1 ? name : t('screens.league.seriesName', { division: name, label: series.label });
  };
}

/** A league fight's time of day, in the viewer's own time zone. */
export function useClockTime() {
  const { i18n } = useTranslation();
  return (ms: number) => {
    try {
      return new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' }).format(ms);
    } catch {
      return new Date(ms).toTimeString().slice(0, 5);
    }
  };
}

/**
 * The countdown to a league fight, to the second: "05:12:33"; a day or more out, "2D 5H" (D50).
 * Rounds up, so it reads 00:00:01 until the fight starts.
 */
export function useFightCountdown() {
  const duration = useDuration();
  return (ms: number) => {
    if (ms >= 86_400_000) return duration(ms);
    const s = Math.max(0, Math.ceil(ms / 1000));
    const two = (n: number) => String(n).padStart(2, '0');
    return `${two(Math.floor(s / 3600))}:${two(Math.floor((s % 3600) / 60))}:${two(s % 60)}`;
  };
}

/**
 * The player's fixture on the next league day, the opponent, and both table rows. None on a day
 * before the player took the place over (`later`: they were placed after its fight began): the
 * place's bot fights that one, so there's no Fight now, countdown or Live for them (D50).
 */
export function todayFixture(view: SeriesView, mySlot: number, joinedDay: number | null) {
  const later = view.next.day > 0 && !fightsOn(joinedDay, view.next.day);
  const fixture = later
    ? undefined
    : view.fixtures.find((f) => f.day === view.next.day && ((f.a.slot === mySlot && !f.a.series) || (f.b.slot === mySlot && !f.b.series)));
  const meSide: 0 | 1 = fixture && fixture.a.slot === mySlot && !fixture.a.series ? 0 : 1;
  const opp = fixture ? (meSide === 0 ? fixture.b : fixture.a) : null;
  const row = (slot: number | undefined) => view.table.find((r) => r.slot === slot);
  return { fixture, later, meSide, opp, me: row(mySlot), oppRow: opp && !opp.series ? row(opp.slot) : undefined };
}

export type FightPhase = 'upcoming' | 'live' | 'closing' | 'none';

/** Where a league day stands at `now`: before the fight, its live window, or after it. */
export function fightPhase(next: DayInfo, now: number): FightPhase {
  if (!next.day) return 'none';
  if (now < next.fightAt) return 'upcoming';
  if (now < next.windowEndsAt) return 'live';
  return 'closing';
}

/** The amber "FIGHT 20:00 · 3H 12M" chip at the top of the league screens; `compact`: "3H 12M". */
export function FightChip({ next, now, compact = false }: { next: DayInfo; now: number; compact?: boolean }) {
  const { t } = useTranslation();
  const time = useClockTime();
  const duration = useDuration();
  const phase = fightPhase(next, now);
  if (phase === 'none') return null;
  const tail = phase === 'upcoming' ? duration(next.fightAt - now) : phase === 'live' ? t('screens.league.live') : t('screens.league.resultsSoon');
  return (
    <div className={[s.fightChip, phase === 'live' ? s.fightChipLive : ''].join(' ')}>
      <span className={s.dot} aria-hidden="true" />
      {compact ? tail : `${t('screens.league.fightAt', { time: time(next.fightAt) })} · ${tail}`}
    </div>
  );
}

/**
 * A flag (emoji) with the country's name for screen readers; `decorative` when the country is
 * said already (the same flag on every row of a league's table). `blank` keeps the flag's room
 * but shows nothing: a bot has no flag (D44), and its row still lines up.
 */
export function Flag({ country, className, decorative = false, blank = false }: { country: string; className?: string; decorative?: boolean; blank?: boolean }) {
  const { i18n } = useTranslation();
  const flag = flagEmoji(country);
  if (!flag) return null;
  const cls = [s.flag, className ?? ''].join(' ');
  if (blank) {
    return (
      <span className={cls} aria-hidden="true" style={{ visibility: 'hidden' }}>
        {flag}
      </span>
    );
  }
  if (decorative) {
    return (
      <span className={cls} aria-hidden="true">
        {flag}
      </span>
    );
  }
  return (
    <span className={cls} role="img" aria-label={countryName(country, i18n.language)}>
      {flag}
    </span>
  );
}

/** "KO · R2", "Decision", "Draw" for a fought fixture; "Pending" or "Live" otherwise. */
export function useResultLabel() {
  const { t } = useTranslation();
  return (f: Pick<FixtureView, 'state' | 'method' | 'round' | 'winner'>) => {
    if (f.state === 'live') return t('screens.league.live');
    if (f.state !== 'done') return t('screens.league.pending');
    if (f.method === 'ko') return t('screens.league.koRound', { round: f.round ?? 1 });
    if (f.method === 'forfeit') return t('screens.league.forfeit');
    if (f.winner === null) return t('screens.league.draw');
    return t('screens.league.decision');
  };
}

/** Damage as the fight counts it, signed ("+2,740", "−310"). */
export function signedHealth(damage: number): string {
  const v = Math.round(damage);
  return v > 0 ? `+${formatNumber(v)}` : v < 0 ? `−${formatNumber(-v)}` : '0';
}
